import { Link } from "react-router-dom";
import { ArrowRight, Image, Video, FileText, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface FeatureHighlightProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  linkTo: string;
  linkText?: string;
  imagePosition: 'left' | 'right';
  index: number;
  icon: typeof Image;
  isVideo?: boolean;
}

export const FeatureHighlight = ({
  title,
  description,
  imageSrc,
  imageAlt,
  linkTo,
  linkText = "Learn More",
  imagePosition,
  index,
  icon: Icon,
  isVideo = false,
}: FeatureHighlightProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;
  const bgClass = isEven ? "bg-background" : "bg-card";

  const imageContent = (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-accent/10 to-accent/5 border border-border/50">
      {imageSrc && !imageSrc.includes('placeholder') ? (
        isVideo ? (
          <video
            src={imageSrc}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            aria-label={imageAlt}
          />
        ) : (
          <img
            src={imageSrc}
            alt={imageAlt}
            className="w-full h-full object-cover"
          />
        )
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6">
          <div className="w-20 h-20 rounded-2xl bg-accent/20 flex items-center justify-center">
            <Icon className="w-10 h-10 text-accent" />
          </div>
          <span className="text-sm text-muted-foreground text-center">
            Animated preview coming soon
          </span>
        </div>
      )}
    </div>
  );

  const textContent = (
    <div className="flex flex-col justify-center space-y-6 py-4">
      <div className="space-y-4">
        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground leading-tight">
          {title}
        </h3>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
          {description}
        </p>
      </div>
      <Link
        to={linkTo}
        className="inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all duration-300 group w-fit"
      >
        {linkText}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );

  return (
    <section
      ref={sectionRef}
      className={`py-16 md:py-24 ${bgClass}`}
    >
      <div className="container mx-auto px-4">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
            isVisible ? 'opacity-100' : 'opacity-0'
          } transition-opacity duration-700`}
          style={{ 
            animationDelay: `${index * 0.15}s`,
          }}
        >
          {/* Mobile: Image always on top */}
          <div className={`lg:hidden ${isVisible ? 'animate-slide-in-up' : ''}`}>
            {imageContent}
          </div>
          <div className={`lg:hidden ${isVisible ? 'animate-slide-in-up' : ''}`} style={{ animationDelay: '0.1s' }}>
            {textContent}
          </div>

          {/* Desktop: Alternating layout */}
          {imagePosition === 'left' ? (
            <>
              <div className={`hidden lg:block ${isVisible ? 'animate-slide-in-left' : ''}`}>
                {imageContent}
              </div>
              <div className={`hidden lg:block ${isVisible ? 'animate-slide-in-right' : ''}`}>
                {textContent}
              </div>
            </>
          ) : (
            <>
              <div className={`hidden lg:block ${isVisible ? 'animate-slide-in-left' : ''}`}>
                {textContent}
              </div>
              <div className={`hidden lg:block ${isVisible ? 'animate-slide-in-right' : ''}`}>
                {imageContent}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
